import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Select,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Divider,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useBreakpointValue,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Progress
} from '@chakra-ui/react';
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  ChevronDownIcon,
  DownloadIcon,
  AttachmentIcon
} from '@chakra-ui/icons';
import {
  FiUpload,
  FiFile,
  FiDownload,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiFilter,
  FiPlus,
  FiFileText,
  FiImage,
  FiVideo,
  FiArchive,
  FiClock,
  FiUser,
  FiCalendar
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import { urlvideo } from './../services/urls';
import SidebarInstructor from '../shared/Sidebar3';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const FileCard = ({ upload, onDownload, onDelete, primaryColor, secondaryColor, textColor, cardBg, user }) => {
  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return FiFileText;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return FiImage;
      case 'mp4':
      case 'avi':
      case 'mov':
        return FiVideo;
      case 'zip':
      case 'rar':
        return FiArchive;
      default:
        return FiFile;
    }
  };

  const getFileSize = (bytes) => {
    if (!bytes) return 'غير محدد';
    const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      default: return 'blue';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'موافق عليه';
      case 'pending': return 'قيد المراجعة';
      case 'rejected': return 'مرفوض';
      default: return 'قيد المراجعة';
    }
  };

  return (
    <motion.div variants={fadeIn}>
      <Card
        bg={cardBg}
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "lg",
          borderColor: primaryColor
        }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <HStack spacing={3} flex="1">
              <Box
                bg={`${primaryColor}20`}
                p={3}
                borderRadius="lg"
              >
                <Icon as={getFileIcon(upload.serverFilename)} color={primaryColor} boxSize={6} />
              </Box>
              <VStack align="flex-start" spacing={1} flex="1">
                <Heading size="sm" color={secondaryColor} fontFamily="Cairo, sans-serif" noOfLines={1}>
                  {upload.serverFilename || 'ملف بدون اسم'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  {getFileSize(upload.size)}
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme={getStatusColor(upload.status)}
              variant="subtle"
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
            >
              {getStatusText(upload.status)}
            </Badge>
          </Flex>
        </CardHeader>

        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            {/* File Info */}
            <VStack spacing={2} align="stretch">
              {upload.uploadNotes && (
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={1}>
                    الملاحظات:
                  </Text>
                  <Text fontSize="sm" color="gray.700" bg="gray.50" p={2} borderRadius="md">
                    {upload.uploadNotes}
                  </Text>
                </Box>
              )}

              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiCalendar} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {upload.createdAt ? 
                      format(new Date(upload.createdAt), 'dd/MM/yyyy', { locale: ar }) : 
                      'غير محدد'}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={FiClock} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {upload.createdAt ? 
                      format(new Date(upload.createdAt), 'HH:mm', { locale: ar }) : 
                      'غير محدد'}
                  </Text>
                </HStack>
              </HStack>

              {upload.courseName && (
                <HStack>
                  <Icon as={FiFileText} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    الدورة: {upload.courseName}
                  </Text>
                </HStack>
              )}

              {upload.feedback && (
                <Box
                  bg={upload.status === 'approved' ? 'green.50' : upload.status === 'rejected' ? 'red.50' : 'blue.50'}
                  p={3}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={upload.status === 'approved' ? 'green.200' : upload.status === 'rejected' ? 'red.200' : 'blue.200'}
                >
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    تعليق المدرب:
                  </Text>
                  <Text fontSize="sm" color={upload.status === 'approved' ? 'green.700' : upload.status === 'rejected' ? 'red.700' : 'blue.700'}>
                    {upload.feedback}
                  </Text>
                </Box>
              )}
            </VStack>

            <Divider />

            {/* Action Buttons */}
            <HStack spacing={2}>
              <Button
                flex="1"
                variant="outline"
                borderColor="gray.300"
                _hover={{ borderColor: primaryColor, color: primaryColor }}
                leftIcon={<FiDownload />}
                onClick={() => window.open(urlvideo+ upload.path)}
              >
                تحميل
              </Button>
              
              <IconButton
                aria-label="عرض التفاصيل"
                icon={<FiEye />}
                variant="outline"
                borderColor="gray.300"
                _hover={{ borderColor: primaryColor, color: primaryColor }}
                size="sm"
                onClick={()=>window.open(urlvideo+upload.path)}
              />
              
              {(user?.isAdmin || upload.userId === user?._id) && (
                <IconButton
                  aria-label="حذف"
                  icon={<FiTrash2 />}
                  variant="outline"
                  borderColor="red.300"
                  color="red.500"
                  _hover={{ borderColor: "red.500", bg: "red.50" }}
                  onClick={() => onDelete(upload)}
                  size="sm"
                />
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </motion.div>
  );
};

const UserUploadsContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });
  
  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [uploads, setUploads] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    courseId: '',
    uploadNotes: ''
  });
  const [isUploading, setIsUploading] = useState(false);

  // Modal states
  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [selectedUpload, setSelectedUpload] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalSize: 0
  });

  useEffect(() => {
    fetchUploads();
    fetchCourses();
  }, []);

  useEffect(() => {
    filterUploads();
  }, [uploads, searchTerm, filterStatus, filterCourse]);

  const fetchUploads = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/upload/course');
      const uploadData = response.data || [];
      
      setUploads(uploadData);
      calculateStats(uploadData);
    } catch (error) {
      console.error('خطأ في جلب الملفات:', error);
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'فشل في تحميل قائمة الملفات',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axiosInstance.get('/courses');
      setCourses(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الدورات:', error);
    }
  };

  const calculateStats = (uploadData) => {
    const total = uploadData.length;
    const pending = uploadData.filter(u => u.status === 'Submitted').length;
    const approved = uploadData.filter(u => u.status === 'Reviewed').length;
    const rejected = uploadData.filter(u => u.status === 'rejected').length;
    const totalSize = uploadData.reduce((sum, u) => sum + (u.size || 0), 0);

    setStats({
      total,
      pending,
      approved,
      rejected,
      totalSize
    });
  };

  const filterUploads = () => {
    let filtered = uploads;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(upload =>
        upload.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.uploadNotes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.courseName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(upload => upload.status === filterStatus);
    }

    // Course filter
    if (filterCourse !== 'all') {
      filtered = filtered.filter(upload => upload.courseId === filterCourse);
    }

    setFilteredUploads(filtered);
  };

  const handleFileUpload = async () => {
    if (!uploadForm.file || !uploadForm.courseId) {
      toast({
        title: 'بيانات ناقصة',
        description: 'يرجى اختيار ملف ودورة',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('uploadNotes', uploadForm.uploadNotes);

      const response = await axiosInstance.post(`/upload/userfile/${uploadForm.courseId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast({
        title: 'تم رفع الملف',
        description: 'تم رفع ملفك بنجاح وسيتم مراجعته قريباً',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setUploadForm({ file: null, courseId: '', uploadNotes: '' });
      onUploadClose();
      fetchUploads();
    } catch (error) {
      console.error('خطأ في رفع الملف:', error);
      toast({
        title: 'خطأ في الرفع',
        description: error.response?.data?.message || 'فشل في رفع الملف',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (upload) => {
    try {
      const response = await axiosInstance.get(`${urlvideo+ upload.path}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', upload.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('خطأ في تحميل الملف:', error);
      toast({
        title: 'خطأ في التحميل',
        description: 'فشل في تحميل الملف',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = (upload) => {
    setSelectedUpload(upload);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/upload/${selectedUpload._id}`);
      
      toast({
        title: 'تم حذف الملف',
        description: 'تم حذف الملف بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onDeleteClose();
      fetchUploads();
    } catch (error) {
      console.error('خطأ في حذف الملف:', error);
      toast({
        title: 'خطأ في الحذف',
        description: 'فشل في حذف الملف',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getFileSize = (bytes) => {

  
    if (!bytes) return 'غير محدد';
    const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Cairo, sans-serif">جاري تحميل الملفات...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <SidebarInstructor />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading 
                      as="h1" 
                      size="xl" 
                      color={secondaryColor} 
                      fontFamily="Cairo, sans-serif"
                      fontWeight="700"
                    >
                      ملفاتي المرفوعة 📁
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      إدارة الملفات التي قمت برفعها للمدربين
                    </Text>
                  </VStack>
                  
                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      borderColor="gray.300"
                      _hover={{ borderColor: primaryColor, color: primaryColor }}
                      leftIcon={<FiRefreshCw />}
                      onClick={fetchUploads}
                      fontFamily="Cairo, sans-serif"
                    >
                      تحديث
                    </Button>
                    <Button
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: `${primaryColor}dd` }}
                      leftIcon={<FiUpload />}
                      onClick={onUploadOpen}
                      fontFamily="Cairo, sans-serif"
                      fontWeight="bold"
                    >
                      رفع ملف جديد
                    </Button>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        إجمالي الملفات
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {stats.total}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        قيد المراجعة
                      </StatLabel>
                      <StatNumber color="yellow.500" fontSize="2xl" fontWeight="bold">
                        {stats.pending}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        موافق عليها
                      </StatLabel>
                      <StatNumber color="green.500" fontSize="2xl" fontWeight="bold">
                        {stats.approved}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        مرفوضة
                      </StatLabel>
                      <StatNumber color="red.500" fontSize="2xl" fontWeight="bold">
                        {stats.rejected}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        الحجم الإجمالي
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="lg" fontWeight="bold">
                        {getFileSize(stats.totalSize)}
                      </StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  gap={4}
                  flexDirection={{ base: 'column', md: 'row' }}
                  align={{ base: 'stretch', md: 'center' }}
                >
                  <InputGroup flex="1" maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input 
                      placeholder="البحث في الملفات..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                    />
                  </InputGroup>
                

                
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Files Grid */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {filteredUploads.length === 0 ? (
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody p={8}>
                  <VStack spacing={4}>
                    <Icon as={FiUpload} w={12} h={12} color="gray.400" />
                    <Heading size="md" color="gray.500" textAlign="center">
                      لا توجد ملفات
                    </Heading>
                    <Text color="gray.400" textAlign="center">
                      {searchTerm || filterStatus !== 'all' || filterCourse !== 'all' 
                        ? 'لم يتم العثور على ملفات تطابق معايير البحث'
                        : 'لم يتم رفع أي ملفات بعد'}
                    </Text>
                    <Button
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: `${primaryColor}dd` }}
                      leftIcon={<FiUpload />}
                      onClick={onUploadOpen}
                    >
                      رفع ملف جديد
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {filteredUploads.map((upload) => (
                  <FileCard
                    key={upload._id}
                    upload={upload}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                    cardBg={cardBg}
                    user={user}
                  />
                ))}
              </SimpleGrid>
            )}
          </motion.div>
        </Box>
      </Flex>

      {/* Upload File Modal */}
      <Modal isOpen={isUploadOpen} onClose={onUploadClose} size="lg">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader fontFamily="Cairo, sans-serif">رفع ملف جديد</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>اختر الدورة</FormLabel>
                <Select
                  value={uploadForm.courseId}
                  onChange={(e) => setUploadForm({ ...uploadForm, courseId: e.target.value })}
                  placeholder="اختر الدورة"
                >
                  {courses.map(course => (
                    <option key={course._id} value={course._id}>
                      {course.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>اختر الملف</FormLabel>
                <Input
                  type="file"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                  p={1}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  الحد الأقصى: 10 ميجابايت. التنسيقات المدعومة: PDF, DOC, DOCX, JPG, PNG, ZIP
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>ملاحظات للمدرب (اختياري)</FormLabel>
                <Textarea
                  value={uploadForm.uploadNotes}
                  onChange={(e) => setUploadForm({ ...uploadForm, uploadNotes: e.target.value })}
                  placeholder="أضف أي ملاحظات حول هذا الملف"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUploadClose}>
              إلغاء
            </Button>
            <Button
              bg={primaryColor}
              color={secondaryColor}
              _hover={{ bg: `${primaryColor}dd` }}
              leftIcon={<FiUpload />}
              onClick={handleFileUpload}
              isLoading={isUploading}
              loadingText="جاري الرفع..."
            >
              رفع الملف
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader fontFamily="Cairo, sans-serif">تأكيد الحذف</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>هل أنت متأكد من حذف هذا الملف؟ لا يمكن التراجع عن هذا الإجراء.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              إلغاء
            </Button>
            <Button colorScheme="red" onClick={confirmDelete}>
              حذف
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const UserUploadsPage = () => {
  return (
    <SidebarProvider>
      <UserUploadsContent />
    </SidebarProvider>
  );
};

export default UserUploadsPage;

